import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Divider, Select,
  MenuItem, FormControl, InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import ImageUpload from '../components/ImageUpload';

export default function Projects() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  const [dialog, setDialog] = useState({ open: false, editId: '' });
  const [form, setForm] = useState({
    name: '', client: '', period: '', budget: '',
    description: '', image: '', departmentId: '', companyId: ''
  });

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [projSnap, deptSnap] = await Promise.all([
      getDocs(collection(db, 'projects')),
      getDocs(collection(db, 'departments')),
    ]);
    setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  function openNew() {
    setForm({ name: '', client: '', period: '', budget: '', description: '', image: '', departmentId: '', companyId: '' });
    setDialog({ open: true, editId: '' });
  }

  function openEdit(project) {
    setForm({
      name: project.name || '',
      client: project.client || '',
      period: project.period || '',
      budget: project.budget || '',
      description: project.description || '',
      image: project.image || '',
      departmentId: project.departmentId || '',
      companyId: project.companyId || ''
    });
    setDialog({ open: true, editId: project.id });
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    const dept = departments.find(d => d.id === form.departmentId);
    const data = {
      ...form,
      companyId: dept?.companyId || '',
      updatedAt: new Date()
    };
    if (dialog.editId) {
      await updateDoc(doc(db, 'projects', dialog.editId), data);
    } else {
      await addDoc(collection(db, 'projects'), {
        ...data,
        createdBy: currentUser.uid,
        createdAt: new Date()
      });
    }
    setSaved('Projekt sparat!');
    setTimeout(() => setSaved(''), 2000);
    setDialog({ open: false, editId: '' });
    loadAll();
  }

  async function handleDelete(id) {
    await deleteDoc(doc(db, 'projects', id));
    loadAll();
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">Projektdatabas</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: 'white', color: '#1a1a1a' }}
            onClick={openNew}>
            Nytt projekt
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

        {departments.map(dept => {
          const deptProjects = projects.filter(p => p.departmentId === dept.id);
          if (deptProjects.length === 0) return null;
          return (
            <Box key={dept.id} sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" color="#1a1a1a" gutterBottom>
                {dept.name}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {deptProjects.map(project => (
                  <Paper key={project.id} sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {project.image && (
                        <Box component="img" src={project.image}
                          sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography fontWeight="bold" variant="h6">{project.name}</Typography>
                          <Box>
                            <IconButton size="small" onClick={() => openEdit(project)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(project.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {project.client && <Chip label={project.client} size="small" />}
                          {project.period && <Chip label={project.period} size="small" variant="outlined" />}
                          {project.budget && <Chip label={project.budget} size="small" color="primary" variant="outlined" />}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {project.description?.substring(0, 200)}{project.description?.length > 200 ? '...' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          );
        })}

        {/* Projekt utan affärsområde */}
        {projects.filter(p => !p.departmentId).length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom>
              Okopplade projekt
            </Typography>
            {projects.filter(p => !p.departmentId).map(project => (
              <Paper key={project.id} sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontWeight="bold">{project.name}</Typography>
                  <Box>
                    <IconButton size="small" onClick={() => openEdit(project)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(project.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Container>

      {/* Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ ...dialog, open: false })}
        maxWidth="md" fullWidth>
        <DialogTitle>{dialog.editId ? 'Redigera projekt' : 'Nytt projekt'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Projektnamn" value={form.name} fullWidth
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Uppdragsgivare" value={form.client} fullWidth
                onChange={e => setForm(prev => ({ ...prev, client: e.target.value }))} />
              <TextField label="Period" value={form.period} fullWidth
                onChange={e => setForm(prev => ({ ...prev, period: e.target.value }))} />
              <TextField label="Budget" value={form.budget} fullWidth
                onChange={e => setForm(prev => ({ ...prev, budget: e.target.value }))} />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Affärsområde</InputLabel>
              <Select value={form.departmentId} label="Affärsområde"
                onChange={e => setForm(prev => ({ ...prev, departmentId: e.target.value }))}>
                <MenuItem value="">Inget affärsområde</MenuItem>
                {departments.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Projektbeskrivning (gemensam text)" value={form.description}
              fullWidth multiline rows={6}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Projektbild</Typography>
            <ImageUpload
              currentUrl={form.image}
              onUpload={url => setForm(prev => ({ ...prev, image: url }))}
              path={'projects/' + (dialog.editId || 'new')}
              label="Ladda upp projektbild"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ ...dialog, open: false })}>Avbryt</Button>
          <Button variant="contained" onClick={handleSave}>Spara</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}